import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  Eye,
  Layers3,
  Package,
  Pencil,
  Plus,
  Save,
  Search,
  Store,
  Trash2,
  X,
} from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ConfirmModal from "../Dashboard/components/ConfirmModal";
import ThemeMessagePopup from "../Dashboard/Products/component/ThemeMessagePopup";
import { restaurantAdminService, rowsOf } from "../../services/restaurantAdmin.service";
import { isBase64Image, resolveRestaurantFoodImageUrl } from "../../utils/restaurantFoodImageUrl";
import FoodCatalogManagerDrawer from "./FoodCatalogManagerDrawer";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function SelectLike({ value, onChange, placeholder, options = [], disabled = false }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = options.find((o) => String(o.value ?? o) === String(value));

  return (
    <div ref={dropdownRef} className="relative min-w-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`h-11 w-full rounded-xl border px-4 pr-10 text-left text-[13px] outline-none transition ${disabled
            ? "cursor-not-allowed border-[#f2c6cc] bg-slate-50 text-slate-400"
            : "border-[#f2c6cc] bg-white text-slate-600 hover:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
          }`}
      >
        {selected ? selected.label ?? selected : placeholder}
      </button>

      <ChevronDown
        size={18}
        className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition ${open ? "rotate-180" : ""}`}
      />

      {open && !disabled ? (
        <div className="absolute z-50 mt-2 max-h-64 w-full overflow-y-auto rounded-xl border border-[#f2c6cc] bg-white shadow-xl">
          <button
            type="button"
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            className="w-full px-4 py-3 text-left text-[13px] text-slate-500 hover:bg-[#fff4f6] hover:text-[#d86a78]"
          >
            {placeholder}
          </button>

          {options.map((o) => {
            const val = o.value ?? o;
            const label = o.label ?? o;
            const active = String(value) === String(val);
            return (
              <button
                key={val}
                type="button"
                onClick={() => {
                  onChange(val);
                  setOpen(false);
                }}
                className={`w-full px-4 py-3 text-left text-[13px] transition ${active ? "bg-[#d86a78] text-white" : "text-slate-600 hover:bg-[#fff4f6] hover:text-[#d86a78]"
                  }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function StatusPill({ active }) {
  const ok = Boolean(active);
  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold ${ok ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
      {ok ? "Active" : "Inactive"}
    </span>
  );
}

function ActiveDot({ active }) {
  const ok = Boolean(active);
  return (
    <span className={`inline-flex items-center gap-2 text-[12px] font-semibold ${ok ? "text-emerald-600" : "text-rose-600"}`}>
      <span className={`h-2 w-2 rounded-full ${ok ? "bg-emerald-500" : "bg-rose-500"}`} />
      {ok ? "Yes" : "No"}
    </span>
  );
}

function Switch({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={
        "relative h-[22px] w-[48px] rounded-full transition focus:outline-none focus:ring-2 focus:ring-[#f7d7db] focus:ring-offset-1 " +
        (disabled ? "cursor-not-allowed opacity-60 " : "") +
        (checked ? "bg-[#d86a78]" : "bg-slate-300")
      }
    >
      <span className={`absolute top-[3px] h-4 w-4 rounded-full bg-white shadow transition ${checked ? "left-[29px]" : "left-[3px]"}`} />
    </button>
  );
}

function AddFoodChoiceModal({ open, onClose, onManual }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-end justify-center bg-black/45 p-3 sm:items-center sm:p-4">
      <div className="relative flex max-h-[90vh] w-full max-w-[780px] flex-col overflow-hidden rounded-[22px] bg-white shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 sm:right-4 sm:top-4"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="border-b border-[#f4d9dd] px-4 py-5 text-center sm:px-8 sm:py-6">
          <h2 className="pr-8 text-[22px] font-bold text-[#d86a78] sm:pr-0 sm:text-[28px]">Add Item</h2>
          <p className="mt-2 text-[13px] text-slate-500 sm:text-[14px]">
            Choose how you want to add a catalog item.
          </p>
        </div>

        <div className="overflow-y-auto px-4 py-5 sm:px-8 sm:py-7">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-1">
            <button
              type="button"
              onClick={onManual}
              className="group rounded-[18px] border border-[#f2c6cc] bg-[#fff8f9] p-5 text-left transition hover:-translate-y-[2px] hover:border-[#d86a78] hover:shadow-lg sm:p-6"
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-[16px] bg-[#fdecef] text-[#d86a78] sm:h-14 sm:w-14">
                <Package size={26} />
              </div>
              <h3 className="text-[18px] font-bold text-slate-800 sm:text-[22px]">Add Item Manually</h3>
              <p className="mt-2 text-[13px] leading-6 text-slate-500 sm:text-[14px]">
                Create one catalog item with category, images, variants, tax and restaurant assignment.
              </p>
              <div className="mt-5 inline-flex items-center rounded-[10px] bg-[#d86a78] px-4 py-2 text-[13px] font-semibold text-white transition group-hover:bg-[#c75a69]">
                Continue
              </div>
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-[#f4d9dd] bg-[#fffafb] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <div className="text-center text-[13px] text-slate-400 sm:text-left">Choose one option to continue</div>
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-[10px] border border-[#f2c6cc] px-5 py-2 text-[13px] font-semibold text-[#d86a78] transition hover:bg-[#fff1f3] sm:w-auto"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 break-words text-[13px] font-medium text-slate-700">{value || "-"}</div>
    </div>
  );
}

function PaginationBar({
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  itemLabel = "items",
}) {
  const safeTotalPages = Math.max(1, totalPages || 1);
  const safePage = Math.min(Math.max(1, page || 1), safeTotalPages);

  if (!totalItems) return null;

  return (
    <div className="mt-5 flex flex-col gap-3 rounded-[20px] border border-[#f2c6cc] bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-[12px] font-medium text-slate-500">
        Total {totalItems} {itemLabel}
      </div>

      <div className="flex flex-wrap items-center justify-start gap-3 sm:justify-end">
        <label className="flex items-center gap-2 text-[12px] font-medium text-slate-500">
          Rows
          <select
            value={pageSize}
            onChange={(e) => {
              onPageSizeChange(Number(e.target.value));
              onPageChange(1);
            }}
            className="h-10 rounded-[12px] border border-[#f2c6cc] bg-white px-3 text-[13px] font-semibold text-slate-700 outline-none transition focus:border-[#d86a78] focus:ring-4 focus:ring-[#d86a78]/10"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, safePage - 1))}
          disabled={safePage <= 1}
          className="h-10 rounded-[12px] border border-[#f2c6cc] bg-white px-4 text-[12px] font-semibold text-[#d86a78] transition hover:bg-[#fff6f7] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Prev
        </button>

        <div className="h-10 rounded-[12px] bg-white px-3 py-2 text-[12px] font-semibold text-slate-600">
          Page {safePage} / {safeTotalPages}
        </div>

        <button
          type="button"
          onClick={() => onPageChange(Math.min(safeTotalPages, safePage + 1))}
          disabled={safePage >= safeTotalPages}
          className="h-10 rounded-[12px] border border-[#f2c6cc] bg-white px-4 text-[12px] font-semibold text-[#d86a78] transition hover:bg-[#fff6f7] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function isComboRow(row) {
  return String(row?.itemKind || row?.item_kind || row?.type || "").toUpperCase() === "COMBO" || Boolean(row?.comboPrice ?? row?.combo_price);
}
function idOf(row) {
  return row?.id || row?.foodItemId || row?.food_item_id || row?.comboId || row?.combo_id || "";
}
function restaurantIdOf(row) {
  return row?.restaurantId || row?.restaurant_id || row?.restaurant?.id || "";
}
function buildRestaurantLookup(restaurants = []) {
  const map = new Map();
  for (const restaurant of Array.isArray(restaurants) ? restaurants : []) {
    const id = restaurant?.id || restaurant?.restaurantId || restaurant?.restaurant_id;
    if (!id) continue;
    map.set(String(id), {
      id: String(id),
      name: restaurant?.name || restaurant?.restaurantName || restaurant?.restaurant_name || restaurant?.businessName || restaurant?.business_name || String(id),
      code: restaurant?.code || restaurant?.restaurantCode || restaurant?.restaurant_code || "",
    });
  }
  return map;
}
function hydrateRestaurantFields(row, restaurantLookup = new Map()) {
  if (!isComboRow(row)) return row;
  const restaurantId = restaurantIdOf(row);
  const restaurant = restaurantId ? restaurantLookup.get(String(restaurantId)) : null;
  if (!restaurant) return row;
  return {
    ...row,
    restaurantId,
    restaurant_id: restaurantId,
    restaurantName: restaurant.name,
    restaurant_name: restaurant.name,
    restaurantCode: restaurant.code || row?.restaurantCode || row?.restaurant_code || "",
    restaurant_code: restaurant.code || row?.restaurantCode || row?.restaurant_code || "",
  };
}
function nameOf(row) {
  return row?.name || row?.foodName || row?.food_name || row?.comboName || row?.combo_name || (isComboRow(row) ? "Combo" : "Food item");
}
function imageOf(row) {
  const url = isComboRow(row)
    ? row?.imageUrl || row?.image_url || row?.items?.[0]?.imageUrl || row?.items?.[0]?.image_url || ""
    : row?.imageUrl || row?.image_url || row?.variants?.[0]?.imageUrl || row?.variants?.[0]?.image_url || "";
  return isBase64Image(url) ? "" : url;
}
function restaurantNameOf(row) {
  const restaurantId = restaurantIdOf(row);
  const value = row?.restaurantName || row?.restaurant_name || row?.restaurant?.name || "";
  if (value && String(value) !== String(restaurantId)) return value;
  return "-";
}
function restaurantCodeOf(row) {
  return row?.restaurantCode || row?.restaurant_code || row?.restaurant?.code || "";
}
function categoryOf(row) {
  return isComboRow(row) ? "Combo" : row?.categoryName || row?.category_name || row?.category || "-";
}
function subcategoryOf(row) {
  return isComboRow(row) ? restaurantNameOf(row) : row?.subcategoryName || row?.subcategory_name || row?.subCategoryName || row?.subCategory || "-";
}
function categoryIdOf(row) {
  return isComboRow(row) ? "" : row?.categoryId || row?.category_id || row?.category?.id || "";
}
function subcategoryIdOf(row) {
  return isComboRow(row) ? "" : row?.subcategoryId || row?.subcategory_id || row?.subcategory?.id || "";
}
function variantsOf(row) {
  return Array.isArray(row?.variants) ? row.variants : [];
}
function comboItemsOf(row) {
  if (Array.isArray(row?.items)) return row.items;
  if (!row?.items) return [];
  try {
    const parsed = JSON.parse(row.items);
    return Array.isArray(parsed) ? parsed : [];
  } catch { }
  return [];
}
function defaultVariant(row) {
  return variantsOf(row).find((v) => v.isDefault || v.is_default) || variantsOf(row)[0] || {};
}
function comboItemsTotal(row) {
  const direct = Number(row?.itemsSellingTotal ?? row?.items_selling_total);
  if (Number.isFinite(direct) && direct > 0) return direct;
  return comboItemsOf(row).reduce((sum, item) => {
    const qty = Math.max(1, Number(item?.qty ?? item?.quantity ?? 1));
    const price = Number(item?.price ?? item?.sellingPrice ?? item?.selling_price ?? 0);
    return sum + (Number.isFinite(price) ? price * qty : 0);
  }, 0);
}
function itemCountOf(row) {
  if (isComboRow(row)) {
    const count = row?.itemCount ?? row?.item_count ?? comboItemsOf(row).length;
    return Number(count || 0);
  }
  return variantsOf(row).length || 0;
}
function skuOf(row) {
  return isComboRow(row) ? restaurantCodeOf(row) || "Combo" : row?.sku || "-";
}
function priceOf(row) {
  if (isComboRow(row)) return Number(row?.comboPrice ?? row?.combo_price ?? row?.price ?? 0);
  const v = defaultVariant(row);
  return Number(v.sellingPrice ?? v.selling_price ?? row?.basePrice ?? row?.base_price ?? 0);
}
function mrpOf(row) {
  if (isComboRow(row)) return Number(row?.mrp ?? row?.comboMrp ?? row?.combo_mrp ?? 0);
  const v = defaultVariant(row);
  return Number(v.mrp ?? row?.mrp ?? 0);
}
function taxOf(row) {
  if (isComboRow(row)) return null;
  const v = defaultVariant(row);
  return Number(v.taxPercent ?? v.tax_percent ?? row?.taxPercent ?? row?.tax_percent ?? 0);
}
function tagsOf(row) {
  if (Array.isArray(row?.tags)) return row.tags;
  if (!row?.tags) return [];
  try {
    const parsed = JSON.parse(row.tags);
    if (Array.isArray(parsed)) return parsed;
  } catch { }
  return String(row.tags).split(",").map((x) => x.trim()).filter(Boolean);
}
function activeOf(row) {
  return row?.isActive ?? row?.is_active ?? String(row?.status || "active").toLowerCase() === "active";
}
function formatMoney(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "-";
}

function FoodCard({ food, switching, onView, onEdit, onDelete, onToggle }) {
  const image = resolveRestaurantFoodImageUrl(imageOf(food));
  const active = activeOf(food);
  return (
    <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <button type="button" onClick={onView} className="overflow-hidden rounded-[12px] border border-slate-200 bg-slate-50">
          {image ? (
            <img
              src={image}
              alt={nameOf(food)}
              className="h-16 w-16 object-contain p-1 sm:h-[72px] sm:w-[72px]"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center text-slate-300 sm:h-[72px] sm:w-[72px]">
              <Package size={24} />
            </div>
          )}
        </button>

        <div className="min-w-0 flex-1">
          <button type="button" onClick={onView} className="block text-left text-[15px] font-bold text-slate-800 transition hover:text-[#d86a78]">
            {nameOf(food)}
          </button>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusPill active={active} />
            <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-500">{isComboRow(food) ? "Combo" : "Food"}</span>
            <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-500">{isComboRow(food) ? `Restaurant: ${restaurantNameOf(food)}` : `SKU: ${skuOf(food)}`}</span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <ActiveDot active={active} />
            <Switch checked={Boolean(active)} disabled={switching} onChange={onToggle} />
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Type" value={isComboRow(food) ? "Combo" : "Food"} />
        <Field label={isComboRow(food) ? "Restaurant" : "Category"} value={subcategoryOf(food)} />
        <Field label={isComboRow(food) ? "Items" : "Variants"} value={itemCountOf(food)} />
        <Field label={isComboRow(food) ? "Items Total" : "Tax"} value={isComboRow(food) ? formatMoney(comboItemsTotal(food)) : `${taxOf(food).toFixed(2)}%`} />
        <Field label="MRP" value={formatMoney(mrpOf(food))} />
        <Field label="Selling Price" value={formatMoney(priceOf(food))} />
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <button type="button" onClick={onView} className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[#f2c6cc] px-4 py-2.5 text-[13px] font-semibold text-[#d86a78] transition hover:bg-[#fff4f6]">
          <Eye size={16} /> View
        </button>
        <button type="button" onClick={onEdit} className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[#f2c6cc] px-4 py-2.5 text-[13px] font-semibold text-[#d86a78] transition hover:bg-[#fff4f6]">
          <Pencil size={16} /> Edit
        </button>
        <button type="button" onClick={onDelete} className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-rose-200 px-4 py-2.5 text-[13px] font-semibold text-rose-700 transition hover:bg-rose-50">
          <Trash2 size={16} /> Delete
        </button>
      </div>
    </div>
  );
}


function ComboModal({ open, mode = "view", combo, saving = false, onClose, onSave }) {
  const [form, setForm] = useState({ name: "", comboPrice: "", mrp: "", description: "", isActive: true, isAvailable: true });
  const editMode = mode === "edit";

  useEffect(() => {
    if (!combo) return;
    setForm({
      name: nameOf(combo),
      comboPrice: String(priceOf(combo) || ""),
      mrp: String(mrpOf(combo) || ""),
      description: combo?.description || "",
      isActive: Boolean(combo?.isActive ?? combo?.is_active ?? true),
      isAvailable: Boolean(combo?.isAvailable ?? combo?.is_available ?? true),
    });
  }, [combo]);

  if (!open || !combo) return null;

  const image = resolveRestaurantFoodImageUrl(imageOf(combo));
  const items = comboItemsOf(combo);
  const total = comboItemsTotal(combo);
  const comboPrice = Number(form.comboPrice || 0);
  const priceInvalid = editMode && total > 0 && Number.isFinite(comboPrice) && comboPrice > total;

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="fixed inset-0 z-[999] flex items-end justify-center bg-black/45 p-3 sm:items-center sm:p-4">
      <div className="relative flex max-h-[92vh] w-full max-w-[920px] flex-col overflow-hidden rounded-[22px] bg-white shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="absolute right-3 top-3 rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:opacity-60"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="border-b border-[#f4d9dd] px-4 py-5 sm:px-8 sm:py-6">
          <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-wide text-[#d86a78]">
            <Layers3 size={15} /> {editMode ? "Edit Combo" : "Combo Details"}
          </div>
          <h2 className="mt-1 pr-8 text-[22px] font-bold text-slate-900 sm:text-[28px]">{nameOf(combo)}</h2>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-[13px] text-slate-500">
            <Store size={14} /> {restaurantNameOf(combo)}
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">{itemCountOf(combo)} item(s)</span>
            <StatusPill active={activeOf(combo)} />
          </p>
        </div>

        <div className="overflow-y-auto px-4 py-5 sm:px-8 sm:py-7">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[220px_1fr]">
            <div>
              <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-[18px] border border-[#f2c6cc] bg-[#fffafb]">
                {image ? <img src={image} alt={nameOf(combo)} className="h-full w-full object-contain p-3" /> : <Layers3 size={44} className="text-[#d86a78]" />}
              </div>
              <div className="mt-3 rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] p-3 text-[12px] text-slate-600">
                <div className="font-bold text-slate-800">Price guard</div>
                <div className="mt-1">Combo price cannot be greater than selected items total.</div>
                <div className="mt-2 font-bold text-[#d86a78]">Items total: ₹{formatMoney(total)}</div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <Field label="Restaurant" value={restaurantNameOf(combo)} />
                <Field label="Combo Price" value={`₹${formatMoney(priceOf(combo))}`} />
                <Field label="MRP" value={`₹${formatMoney(mrpOf(combo))}`} />
              </div>

              {editMode ? (
                <div className="rounded-[18px] border border-[#f2c6cc] bg-[#fffafb] p-4">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <label className="text-[12px] font-semibold text-slate-600 md:col-span-3">
                      Combo Name
                      <input
                        value={form.name}
                        onChange={(e) => setField("name", e.target.value)}
                        className="mt-1 h-11 w-full rounded-xl border border-[#f2c6cc] bg-white px-4 text-[13px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
                      />
                    </label>
                    <label className="text-[12px] font-semibold text-slate-600">
                      Combo Price
                      <input
                        type="number"
                        value={form.comboPrice}
                        onChange={(e) => setField("comboPrice", e.target.value)}
                        className={`mt-1 h-11 w-full rounded-xl border bg-white px-4 text-[13px] outline-none focus:ring-2 ${priceInvalid ? "border-rose-300 focus:border-rose-500 focus:ring-rose-100" : "border-[#f2c6cc] focus:border-[#d86a78] focus:ring-[#f7d7db]"}`}
                      />
                    </label>
                    <label className="text-[12px] font-semibold text-slate-600">
                      MRP
                      <input
                        type="number"
                        value={form.mrp}
                        onChange={(e) => setField("mrp", e.target.value)}
                        className="mt-1 h-11 w-full rounded-xl border border-[#f2c6cc] bg-white px-4 text-[13px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
                      />
                    </label>
                    <div className="flex items-end gap-3 rounded-xl border border-[#f2c6cc] bg-white px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] font-semibold text-slate-600">Active</div>
                        <div className="text-[11px] text-slate-400">Visible only when active</div>
                      </div>
                      <Switch checked={Boolean(form.isActive)} disabled={saving} onChange={(v) => { setField("isActive", v); setField("isAvailable", v); }} />
                    </div>
                    <label className="text-[12px] font-semibold text-slate-600 md:col-span-3">
                      Description
                      <textarea
                        value={form.description}
                        onChange={(e) => setField("description", e.target.value)}
                        className="mt-1 min-h-[92px] w-full rounded-xl border border-[#f2c6cc] bg-white px-4 py-3 text-[13px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
                      />
                    </label>
                  </div>
                  {priceInvalid ? <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[12px] font-semibold text-rose-700">Combo price cannot be greater than selected items total ₹{formatMoney(total)}.</div> : null}
                </div>
              ) : combo?.description ? (
                <div className="rounded-[18px] border border-[#f2c6cc] bg-[#fffafb] p-4 text-[13px] leading-6 text-slate-600">{combo.description}</div>
              ) : null}

              <div className="overflow-hidden rounded-[18px] border border-[#f2c6cc] bg-white">
                <div className="border-b border-[#f2c6cc] bg-[#fff4f6] px-4 py-3 text-[13px] font-bold text-slate-700">Combo Items</div>
                <div className="overflow-x-auto">
                  <table className="min-w-[680px] w-full text-[13px]">
                    <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-400">
                      <tr>
                        <th className="px-4 py-3">Item</th>
                        <th className="px-4 py-3">Variant</th>
                        <th className="px-4 py-3">Qty</th>
                        <th className="px-4 py-3">Price</th>
                        <th className="px-4 py-3">Line Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length ? items.map((item, index) => {
                        const qty = Math.max(1, Number(item?.qty ?? item?.quantity ?? 1));
                        const price = Number(item?.price ?? item?.sellingPrice ?? item?.selling_price ?? 0);
                        return (
                          <tr key={item?.id || `${item?.foodItemId || item?.food_item_id || "item"}-${index}`} className="border-t border-slate-100">
                            <td className="px-4 py-3 font-semibold text-slate-700">{item?.name || item?.foodName || item?.food_name || "Food item"}</td>
                            <td className="px-4 py-3 text-slate-500">{item?.variantName || item?.variant_name || "-"}</td>
                            <td className="px-4 py-3 text-slate-500">{qty}</td>
                            <td className="px-4 py-3 text-slate-500">₹{formatMoney(price)}</td>
                            <td className="px-4 py-3 font-semibold text-slate-700">₹{formatMoney(price * qty)}</td>
                          </tr>
                        );
                      }) : (
                        <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No combo items found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-[#f4d9dd] bg-[#fffafb] px-4 py-4 sm:flex-row sm:justify-end sm:px-8">
          <button type="button" onClick={onClose} disabled={saving} className="rounded-[10px] border border-[#f2c6cc] px-5 py-2 text-[13px] font-semibold text-[#d86a78] transition hover:bg-[#fff1f3] disabled:opacity-60">Close</button>
          {editMode ? (
            <button
              type="button"
              disabled={saving || priceInvalid}
              onClick={() => onSave?.({
                name: form.name,
                comboPrice: Number(form.comboPrice || 0),
                mrp: Number(form.mrp || form.comboPrice || 0),
                description: form.description,
                isActive: Boolean(form.isActive),
                isAvailable: Boolean(form.isAvailable),
              })}
              className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#d86a78] px-5 py-2 text-[13px] font-semibold text-white transition hover:bg-[#c95b69] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Save size={16} /> {saving ? "Saving..." : "Save Combo"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function FoodItemsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [debouncedQ, setDebouncedQ] = useState(() => searchParams.get("q") || "");
  const [status, setStatus] = useState(() => searchParams.get("status") || "");
  const [itemType, setItemType] = useState(() => searchParams.get("type") || "");
  const [categoryId, setCategoryId] = useState(() => searchParams.get("category") || "");
  const [subcategoryId, setSubcategoryId] = useState(() => searchParams.get("subcategory") || "");
  const [restaurantLookup, setRestaurantLookup] = useState(() => new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [addChoiceOpen, setAddChoiceOpen] = useState(false);
  const [deletePopup, setDeletePopup] = useState({ open: false, row: null });
  const [mutating, setMutating] = useState({ deleting: false, activeIds: new Set() });
  const [messagePopup, setMessagePopup] = useState({ open: false, type: "info", title: "", message: "" });
  const [comboModal, setComboModal] = useState({ open: false, mode: "view", row: null });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const returnTo = `${location.pathname}${location.search || ""}`;

  function showMessage(type, title, message) {
    setMessagePopup({ open: true, type, title, message });
    window.clearTimeout(showMessage._timer);
    showMessage._timer = window.setTimeout(() => {
      setMessagePopup((prev) => ({ ...prev, open: false }));
    }, 3000);
  }

  const categoryOptions = useMemo(() => {
    return (categories || [])
      .map((c) => ({ value: c.id || c.categoryId || c.category_id, label: c.name || c.categoryName || c.category_name || "Category" }))
      .filter((c) => c.value)
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [categories]);

  const subcategoryOptions = useMemo(() => {
    return (subcategories || [])
      .filter((s) => !categoryId || String(s.categoryId || s.category_id) === String(categoryId))
      .map((s) => ({ value: s.id || s.subcategoryId || s.subcategory_id, label: s.name || s.subcategoryName || s.subcategory_name || "Subcategory" }))
      .filter((s) => s.value)
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [categoryId, subcategories]);

  const statusOptions = useMemo(
    () => [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
    ],
    []
  );


  const itemTypeOptions = useMemo(
    () => [
      { value: "food", label: "Food" },
      { value: "combo", label: "Combo" },
    ],
    []
  );

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q), 350);
    return () => window.clearTimeout(t);
  }, [q]);

  async function loadMeta() {
    const [catRes, subRes] = await Promise.all([
      restaurantAdminService.listFoodCategories({ includeInactive: true }),
      restaurantAdminService.listFoodSubcategories({ includeInactive: true }),
    ]);
    setCategories(rowsOf(catRes));
    setSubcategories(rowsOf(subRes));
  }

  async function loadRestaurantLookup() {
    try {
      const firstRes = await restaurantAdminService.listRestaurants({ page: 1, limit: 100 });
      const firstRows = rowsOf(firstRes);
      const meta = firstRes?.data && !Array.isArray(firstRes.data) ? firstRes.data : firstRes || {};
      const totalPages = Math.min(25, Math.max(1, Number(meta.totalPages || meta.total_pages || 1)));
      const extraRows = [];

      if (totalPages > 1) {
        const extraResponses = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, index) =>
            restaurantAdminService.listRestaurants({ page: index + 2, limit: 100 }).catch(() => null)
          )
        );
        for (const response of extraResponses) extraRows.push(...rowsOf(response));
      }

      const lookup = buildRestaurantLookup([...firstRows, ...extraRows]);
      setRestaurantLookup(lookup);
      return lookup;
    } catch {
      return restaurantLookup;
    }
  }

  async function loadItems(next = { q: debouncedQ, status, categoryId, subcategoryId, itemType }) {
    const cleanQ = next.q?.trim() || "";
    const type = next.itemType || "";
    const tasks = [];
    const comboLookup = type !== "food" ? await loadRestaurantLookup() : restaurantLookup;

    if (type !== "combo") {
      tasks.push(
        restaurantAdminService.listFoodItems({
          q: cleanQ,
          status: next.status || "",
          categoryId: next.categoryId || "",
          subcategoryId: next.subcategoryId || "",
          limit: 500,
        }).then((res) => rowsOf(res).map((row) => ({ ...row, itemKind: "FOOD" })))
      );
    }

    if (type !== "food") {
      tasks.push(
        restaurantAdminService.listFoodCombos({
          q: cleanQ,
          status: next.status || "",
          limit: 500,
        }).then((res) => rowsOf(res).map((row) => ({ ...row, itemKind: "COMBO" })))
      );
    }

    const groups = await Promise.all(tasks);
    setRows(groups.flat().map((row) => hydrateRestaurantFields(row, comboLookup)));
  }

  async function loadAll(next) {
    setLoading(true);
    setError("");
    try {
      await Promise.all([loadMeta(), loadItems(next)]);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Unable to load food catalog");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (status) params.set("status", status);
    if (itemType) params.set("type", itemType);
    if (categoryId) params.set("category", categoryId);
    if (subcategoryId) params.set("subcategory", subcategoryId);
    setSearchParams(params, { replace: true });
  }, [q, status, itemType, categoryId, subcategoryId, setSearchParams]);

  useEffect(() => {
    loadAll({ q: debouncedQ, status, itemType, categoryId, subcategoryId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, status, itemType, categoryId, subcategoryId]);

  useEffect(() => {
    if (!subcategoryId || !subcategories.length) return;
    const selected = subcategories.find((s) => String(s.id || s.subcategoryId || s.subcategory_id) === String(subcategoryId));
    if (!selected) return;
    const parentId = selected.categoryId || selected.category_id || "";
    if (categoryId && String(parentId) !== String(categoryId)) {
      setSubcategoryId("");
    }
  }, [subcategoryId, categoryId, subcategories]);

  const visibleRows = useMemo(() => {
    const needle = String(debouncedQ || "").trim().toLowerCase();
    return (rows || []).filter((row) => {
      const active = activeOf(row);
      if (status === "active" && !active) return false;
      if (status === "inactive" && active) return false;
      if (itemType === "food" && isComboRow(row)) return false;
      if (itemType === "combo" && !isComboRow(row)) return false;

      const rowCategoryId = categoryIdOf(row);
      const rowSubcategoryId = subcategoryIdOf(row);
      if (categoryId && (!rowCategoryId || String(rowCategoryId) !== String(categoryId))) return false;
      if (subcategoryId && (!rowSubcategoryId || String(rowSubcategoryId) !== String(subcategoryId))) return false;

      if (!needle) return true;
      const haystack = [
        nameOf(row),
        skuOf(row),
        row?.barcode,
        categoryOf(row),
        subcategoryOf(row),
        restaurantNameOf(row),
        ...comboItemsOf(row).map((item) => `${item?.name || item?.foodName || ""} ${item?.variantName || ""}`),
        ...tagsOf(row),
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return haystack.includes(needle);
    });
  }, [rows, debouncedQ, status, itemType, categoryId, subcategoryId]);

  const totalPages = Math.max(1, Math.ceil(visibleRows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginatedRows = useMemo(
    () => visibleRows.slice((safePage - 1) * pageSize, safePage * pageSize),
    [visibleRows, safePage, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, status, itemType, categoryId, subcategoryId]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  function openFoodDetails(foodId) {
    navigate(`/admin/food-dashboard/items/${foodId}`, { state: { returnTo } });
  }

  function openFoodEdit(foodId) {
    navigate(`/admin/food-dashboard/items/${foodId}/edit`, { state: { returnTo } });
  }

  function openItemDetails(row) {
    const id = idOf(row);
    if (isComboRow(row)) {
      setComboModal({ open: true, mode: "view", row });
      return;
    }
    openFoodDetails(id);
  }

  function openItemEdit(row) {
    const id = idOf(row);
    if (isComboRow(row)) {
      setComboModal({ open: true, mode: "edit", row });
      return;
    }
    openFoodEdit(id);
  }

  async function handleSaveCombo(payload) {
    const row = comboModal.row;
    const id = idOf(row);
    if (!id) return;
    const total = comboItemsTotal(row);
    if (total > 0 && Number(payload.comboPrice || 0) > total) {
      showMessage("error", "Invalid Combo Price", `Combo price cannot be greater than selected items total ₹${formatMoney(total)}.`);
      return;
    }
    try {
      setMutating((prev) => ({ ...prev, deleting: true }));
      const res = await restaurantAdminService.updateFoodCombo(id, payload);
      const updated = hydrateRestaurantFields({ ...row, ...(res?.data || res || {}), itemKind: "COMBO" }, restaurantLookup);
      setRows((prev) => prev.map((item) => String(idOf(item)) === String(id) ? updated : item));
      setComboModal({ open: false, mode: "view", row: null });
      showMessage("success", "Combo Updated", `${nameOf(updated)} has been updated successfully.`);
    } catch (e) {
      showMessage("error", "Combo Update Failed", e?.response?.data?.message || e?.data?.message || e?.message || "Failed to update combo.");
    } finally {
      setMutating((prev) => ({ ...prev, deleting: false }));
    }
  }

  async function handleToggleActive(row, nextValue) {
    const id = idOf(row);
    if (!id) return;
    try {
      setMutating((prev) => ({ ...prev, activeIds: new Set([...prev.activeIds, id]) }));
      if (isComboRow(row)) {
        await restaurantAdminService.setFoodComboStatus(id, { isActive: nextValue, isAvailable: nextValue });
      } else {
        await restaurantAdminService.setFoodItemStatus(id, nextValue);
      }
      setRows((prev) =>
        prev.map((item) =>
          String(idOf(item)) === String(id)
            ? { ...item, isActive: nextValue, is_active: nextValue, isAvailable: nextValue, is_available: nextValue, status: nextValue ? "active" : "inactive" }
            : item
        )
      );
      const label = isComboRow(row) ? "Combo" : "Food";
      showMessage(
        nextValue ? "success" : "info",
        nextValue ? `${label} Activated` : `${label} Deactivated`,
        nextValue ? `${nameOf(row)} has been activated successfully.` : `${nameOf(row)} has been deactivated successfully.`
      );
    } catch (e) {
      showMessage("error", "Status Update Failed", e?.response?.data?.message || e?.data?.message || e?.message || "Failed to update status.");
    } finally {
      setMutating((prev) => {
        const next = new Set(prev.activeIds);
        next.delete(id);
        return { ...prev, activeIds: next };
      });
    }
  }

  async function confirmDeleteFood() {
    const row = deletePopup.row;
    const id = idOf(row);
    if (!id) return setDeletePopup({ open: false, row: null });
    try {
      setMutating((prev) => ({ ...prev, deleting: true }));
      if (isComboRow(row)) {
        await restaurantAdminService.deleteFoodCombo(id);
      } else {
        await restaurantAdminService.deleteFoodItem(id);
      }
      setRows((prev) => prev.filter((item) => String(idOf(item)) !== String(id)));
      showMessage("success", isComboRow(row) ? "Combo Deleted" : "Item Deleted", `${nameOf(row)} has been deleted successfully.`);
      setDeletePopup({ open: false, row: null });
    } catch (e) {
      showMessage("error", "Delete Failed", e?.response?.data?.message || e?.data?.message || e?.message || "Delete failed.");
    } finally {
      setMutating((prev) => ({ ...prev, deleting: false }));
    }
  }

  return (
    <div className="w-full max-w-full overflow-hidden px-0">
      <ConfirmModal
        open={deletePopup.open}
        icon="trash"
        variant="confirm"
        title={`Are you Sure You want to Delete ${nameOf(deletePopup.row || {})}`}
        description={isComboRow(deletePopup.row) ? "This will remove this combo from users and the restaurant menu. Old order history will stay safe. Would you like to proceed?" : "This will remove this food item from users, restaurants, combos and carts. Old order history will stay safe. Would you like to proceed?"}
        onClose={() => {
          if (mutating.deleting) return;
          setDeletePopup({ open: false, row: null });
        }}
        onConfirm={confirmDeleteFood}
        cancelText="NO"
        confirmText={mutating.deleting ? "Deleting…" : "YES"}
      />

      <ThemeMessagePopup
        open={messagePopup.open}
        type={messagePopup.type}
        title={messagePopup.title}
        message={messagePopup.message}
        onClose={() => setMessagePopup((prev) => ({ ...prev, open: false }))}
      />

      <ComboModal
        open={comboModal.open}
        mode={comboModal.mode}
        combo={comboModal.row}
        saving={mutating.deleting}
        onClose={() => {
          if (mutating.deleting) return;
          setComboModal({ open: false, mode: "view", row: null });
        }}
        onSave={handleSaveCombo}
      />

      <AddFoodChoiceModal
        open={addChoiceOpen}
        onClose={() => setAddChoiceOpen(false)}
        onManual={() => {
          setAddChoiceOpen(false);
          navigate("/admin/food-dashboard/items/new");
        }}
      />

      <FoodCatalogManagerDrawer
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        onSaved={() => loadAll({ q: debouncedQ, status, itemType, categoryId, subcategoryId })}
      />

      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-[22px] font-bold tracking-wide text-[#d86a78] sm:text-[25px]">Food Catalog</h1>
          <p className="mt-1 text-[13px] text-slate-500">Manage menu items, combos, pricing, restaurant mapping, and availability.</p>
        </div>
      </div>

      <div className="mt-5 rounded-[20px] border border-[#f2c6cc] bg-[#fffafb] p-3 sm:p-4 lg:p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <div className="relative w-full">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by food name, combo, restaurant, SKU, category or tag"
                className="h-11 w-full rounded-xl border border-[#f2c6cc] bg-white pl-11 pr-4 text-[13px] text-slate-600 outline-none transition focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
              />
              <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-300" />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <SelectLike value={status} onChange={setStatus} placeholder="Status" options={statusOptions} />
              <SelectLike
                value={itemType}
                onChange={(v) => {
                  setItemType(v);
                  if (v === "combo") {
                    setCategoryId("");
                    setSubcategoryId("");
                  }
                }}
                placeholder="Item Type"
                options={itemTypeOptions}
              />
              <SelectLike
                value={categoryId}
                onChange={(v) => {
                  setCategoryId(v);
                  setSubcategoryId("");
                }}
                placeholder={itemType === "combo" ? "Category not used for combos" : "Category"}
                options={categoryOptions}
                disabled={itemType === "combo"}
              />
              <SelectLike
                value={subcategoryId}
                onChange={setSubcategoryId}
                placeholder={itemType === "combo" ? "Subcategory not used for combos" : categoryId ? "Subcategory" : "Select category first"}
                options={subcategoryOptions}
                disabled={itemType === "combo" || !categoryId}
              />
            </div>
          </div>

          <div className="flex w-full flex-col gap-3 sm:flex-row xl:w-auto xl:justify-end">
            <button
              type="button"
              onClick={() => setCatalogOpen(true)}
              className="inline-flex h-11 items-center justify-center rounded-full border border-[#f2c6cc] bg-white px-5 text-[13px] font-semibold text-[#d86a78] shadow-sm transition hover:bg-[#fff7f8]"
            >
              Manage Catalog
            </button>
            <button
              type="button"
              onClick={() => setAddChoiceOpen(true)}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-[#d86a78] px-5 text-[13px] font-semibold text-white transition hover:bg-[#c95b69]"
            >
              <Plus size={16} /> Add Item
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">
          {error}
        </div>
      ) : null}

      <div className="mt-5 hidden overflow-hidden rounded-[18px] border border-[#f2c6cc] bg-white lg:block">
        <div className="overflow-x-auto">
          <table className="min-w-[1280px] w-full">
            <thead>
              <tr className="bg-[#fff4f6] text-left text-[12px] font-bold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-4">Image</th>
                <th className="px-4 py-4">Name</th>
                <th className="px-4 py-4">Type</th>
                <th className="px-4 py-4">SKU</th>
                <th className="px-4 py-4">Category</th>
                <th className="px-4 py-4">{itemType === "combo" ? "Restaurant" : itemType === "food" ? "Subcategory" : "Subcategory / Restaurant"}</th>
                {/* <th className="px-4 py-4">Tags</th> */}
                <th className="px-4 py-4">Variants</th>
                <th className="px-4 py-4">Tax</th>
                <th className="px-4 py-4">MRP</th>
                <th className="px-4 py-4">Selling Price</th>
                <th className="px-4 py-4">Status</th>
                <th className="px-4 py-4">Active</th>
                <th className="px-4 py-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={13} className="px-4 py-12 text-center text-[13px] text-slate-500">Loading food catalog...</td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-4 py-12 text-center text-[13px] text-slate-500">No food or combo found.</td>
                </tr>
              ) : (
                paginatedRows.map((row) => {
                  const id = idOf(row);
                  const image = resolveRestaurantFoodImageUrl(imageOf(row));
                  const active = activeOf(row);
                  const switching = mutating.activeIds.has(id);
                  return (
                    <tr key={id} className="border-t border-slate-100 text-[13px] text-slate-600 transition hover:bg-[#fffafb]">
                      <td className="px-4 py-4">
                        <button type="button" onClick={() => openItemDetails(row)} className="overflow-hidden rounded-[10px] border border-slate-200 bg-slate-50">
                          {image ? (
                            <img
                              src={image}
                              alt={nameOf(row)}
                              className="h-[54px] w-[54px] object-contain p-1"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="flex h-[54px] w-[54px] items-center justify-center text-slate-300">
                              <Package size={22} />
                            </div>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-4 max-w-[240px]">
                        <button
                          type="button"
                          onClick={() => openItemDetails(row)}
                          title={nameOf(row)}
                          className="block w-full truncate whitespace-nowrap text-left font-semibold text-slate-700 transition hover:text-[#d86a78]"
                        >
                          {nameOf(row)}
                        </button>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-bold ${isComboRow(row) ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                          {isComboRow(row) ? "Combo" : "Food"}
                        </span>
                      </td>
                      <td className="px-4 py-4">{skuOf(row)}</td>
                      <td className="px-4 py-4">{categoryOf(row)}</td>
                      <td className="px-4 py-4">{subcategoryOf(row)}</td>
                      {/* <td className="px-4 py-4">
                        <div className="flex max-w-[220px] flex-wrap gap-1">
                          {tagsOf(row).length ? tagsOf(row).slice(0, 3).map((tag) => (
                            <span key={tag} className="rounded-full bg-[#fff0f2] px-2 py-1 text-[10px] font-bold text-[#d86a78]">{tag}</span>
                          )) : "-"}
                        </div>
                      </td> */}
                      <td className="px-4 py-4">{itemCountOf(row)}</td>
                      <td className="px-4 py-4">{isComboRow(row) ? `₹${formatMoney(comboItemsTotal(row))}` : `${taxOf(row).toFixed(2)}%`}</td>
                      <td className="px-4 py-4">₹{formatMoney(mrpOf(row))}</td>
                      <td className="px-4 py-4 font-semibold text-slate-700">₹{formatMoney(priceOf(row))}</td>
                      <td className="px-4 py-4"><StatusPill active={active} /></td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          {/* <ActiveDot active={active} /> */}
                          <Switch checked={Boolean(active)} disabled={switching} onChange={(nextValue) => handleToggleActive(row, nextValue)} />
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={() => openItemDetails(row)} className="rounded-[10px] border border-[#f2c6cc] p-2 text-[#d86a78] transition hover:bg-[#fff4f6]" title="View">
                            <Eye size={16} />
                          </button>
                          <button type="button" onClick={() => openItemEdit(row)} className="rounded-[10px] border border-[#f2c6cc] p-2 text-[#d86a78] transition hover:bg-[#fff4f6]" title="Edit">
                            <Pencil size={16} />
                          </button>
                          <button type="button" onClick={() => setDeletePopup({ open: true, row })} className="rounded-[10px] border border-rose-200 p-2 text-rose-700 transition hover:bg-rose-50" title="Delete">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-5 space-y-4 lg:hidden">
        {loading ? (
          <div className="rounded-[18px] border border-[#f2c6cc] bg-white px-4 py-10 text-center text-[13px] text-slate-500">Loading food catalog...</div>
        ) : visibleRows.length === 0 ? (
          <div className="rounded-[18px] border border-[#f2c6cc] bg-white px-4 py-10 text-center text-[13px] text-slate-500">No food or combo found.</div>
        ) : (
          paginatedRows.map((row) => {
            const id = idOf(row);
            const switching = mutating.activeIds.has(id);
            return (
              <FoodCard
                key={id}
                food={row}
                switching={switching}
                onView={() => openItemDetails(row)}
                onEdit={() => openItemEdit(row)}
                onDelete={() => setDeletePopup({ open: true, row })}
                onToggle={(nextValue) => handleToggleActive(row, nextValue)}
              />
            );
          })
        )}
      </div>

      <PaginationBar
        page={safePage}
        totalPages={totalPages}
        totalItems={visibleRows.length}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        itemLabel="food/combo items"
      />
    </div>
  );
}
